import { Product } from '@/api/generated/api.enum';
import type { TTranslate } from '@/interfaces';
import { applyPercentDiscount } from 'common/utils/billing.utils';
import { formatAmount } from './formatting';
import { getLowestSeatPriceCents, getPricingParts, toMonthlyEquivalentCents } from './pricing-math';
import { type BillingInterval, type ProductWithMeta } from './types';

export function getOnboardingPlanPriceParts(
  product: ProductWithMeta,
  t: TTranslate,
): { amount: string; detail: string } {
  const { currency, yearly } = getPricingParts(product);
  if (product.product === Product.school) {
    return {
      amount: t('onboarding.checkout.priceFrom', {
        price: formatAmount(getLowestSeatPriceCents(yearly ?? {}), currency, { spaced: true }),
      }),
      detail: t('onboarding.checkout.perStudentPerYear'),
    };
  }

  return {
    amount: formatAmount(toMonthlyEquivalentCents(getLowestSeatPriceCents(yearly ?? {})), currency, { spaced: true }),
    detail: t('onboarding.checkout.perTeacherPerMonth'),
  };
}

export function getOnboardingPlanPriceLabel(product: ProductWithMeta, t: TTranslate): string {
  const { amount, detail } = getOnboardingPlanPriceParts(product, t);
  return `${amount} ${detail}`;
}

export const monthlyPrice = (
  plan: ProductWithMeta,
  discountInPercent: number = 0,
  interval: BillingInterval = 'year',
): string => {
  const { currency, yearly, monthly } = getPricingParts(plan);

  if (interval === 'month' && monthly) {
    const baseCents = plan.supportsSeats ? getLowestSeatPriceCents(monthly) : monthly.amountCents;
    const discountedCents = discountInPercent > 0 ? applyPercentDiscount(baseCents, discountInPercent) : baseCents;
    return formatAmount(discountedCents, currency);
  }

  if (plan.product === Product.school) {
    const yearlyBaseCents = getLowestSeatPriceCents(yearly ?? {});
    const yearlyDiscountedCents =
      discountInPercent > 0 ? applyPercentDiscount(yearlyBaseCents, discountInPercent) : yearlyBaseCents;
    return formatAmount(yearlyDiscountedCents, currency);
  }

  const yearlyBaseCents = plan.supportsSeats ? getLowestSeatPriceCents(yearly ?? {}) : (yearly?.amountCents ?? 0);
  const yearlyDiscountedCents =
    discountInPercent > 0 ? applyPercentDiscount(yearlyBaseCents, discountInPercent) : yearlyBaseCents;
  const monthlyCents = yearlyDiscountedCents / 12;

  return formatAmount(monthlyCents, currency);
};

export const monthlyUnitKey = (plan: ProductWithMeta, interval: BillingInterval = 'year'): string => {
  if (plan.product === Product.school) {
    return interval === 'month'
      ? 'billing.products.units.perStudentPerMonth'
      : 'billing.products.units.perStudentPerYear';
  }
  if (plan.product === Product.team) return 'billing.products.units.perTeacherPerMonth';
  return 'billing.products.units.perMonth';
};

export const yearlySavingsPercent = 50;
